'use client';

import { Skeleton } from '@common/components';
import { Lottie } from '@common/components/atom/Lottie';
import SvgChevronright from '@common/icon/SvgChevronright';
import { cn } from '@core/utils';

import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { Rating } from '@/components/molecule/Rating';
import { DONATION_ROUTES } from '@/constants';
import { GOODS_HOME_IMAGE_URL } from '@/constants/yearEndDonationHome';

import { GIFT_CARD_IMAGE_SIZES } from './giftImage';
import { readTappedThumbnail, rememberGiftPreview } from './giftPreview';

interface GiftCardProps {
  /**
   * 기본값은 상세 히어로와 공유하는 `GIFT_CARD_IMAGE_SIZES`다.
   * 바꾸면 상세 진입 시 같은 이미지를 다시 내려받으므로 그대로 두는 것을 권한다.
   */
  mainImageSizes?: string;
  mainImageUrl?: string;
  productName?: string;
  donationAmount?: number;
  reviewRating?: number;
  reviewCount?: number;
  institutionName?: string;
  institution?: {
    id: number | string;
    logoUrl: string;
    name: string;
  };
  productNumber?: string;
  className?: string;
  isLoading?: boolean;
  isPick?: boolean;
  isNew?: boolean;
  isAdult?: boolean;
  isSoldOut?: boolean;
  hasEditionBadge?: boolean;
  ratingSize?: number;
  showRating?: boolean;
  priority?: boolean;
  onGiftLinkClick?: () => void;
  mobileLayout?: 'card' | 'row';
}

export const GiftCard = ({
  mainImageSizes = GIFT_CARD_IMAGE_SIZES,
  mainImageUrl,
  productName,
  donationAmount,
  reviewRating,
  reviewCount,
  institutionName,
  institution,
  productNumber,
  className,
  isLoading,
  isPick,
  isNew,
  isAdult,
  isSoldOut,
  hasEditionBadge = false,
  ratingSize = 12,
  showRating = true,
  priority = false,
  onGiftLinkClick,
  mobileLayout = 'card',
}: GiftCardProps) => {
  const isMobileRow = mobileLayout === 'row';

  if (isLoading) return <Loading isMobileRow={isMobileRow} />;

  return (
    <article
      className={cn(
        'group relative flex flex-col gap-8 rounded-xlarge transition-opacity duration-200 ease-out',
        isMobileRow &&
          'mobile-sm:flex-row mobile-sm:items-center mobile-sm:gap-16',
        'has-[[data-card-link]:active]:opacity-80',
        className,
      )}
    >
      <CustomLink
        data-card-link
        prefetch={false}
        href={
          productNumber
            ? DONATION_ROUTES.GIFTS_DETAIL.pathname({
                productNumber,
              })
            : '#'
        }
        aria-label={`${productName} 상세 페이지로 이동`}
        onClick={(event) => {
          if (productNumber) {
            rememberGiftPreview({
              productNumber,
              mainImageUrl,
              thumbnailSrc: readTappedThumbnail(event.currentTarget),
              productName,
              //* 소비처가 institution 객체만 넘기면 프리뷰에서 지자체명이 비어 카드와 어긋난다
              institutionName: institutionName ?? institution?.name,
              donationAmount,
            });
          }

          onGiftLinkClick?.();
        }}
        className={cn(
          'absolute inset-0 z-10 rounded-xlarge',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500',
          {
            'pointer-events-none': !productNumber,
          },
        )}
      />
      <div
        className={cn(
          'relative aspect-square w-full rounded-xlarge',
          isMobileRow && 'mobile-sm:w-116 mobile-sm:shrink-0',
        )}
      >
        <div className="relative size-full overflow-hidden rounded-xlarge bg-Gray-black/10">
          <Image
            src={mainImageUrl || ''}
            alt={productName ?? '답례품 이미지'}
            className="object-cover"
            sizes={mainImageSizes}
            preload={priority}
            fill
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-Gray-black/0 transition-colors duration-300 ease-out group-hover:bg-Gray-black/5"
          />
        </div>
        {isNew && (
          <div className="absolute left-8 top-8 flex">
            <NewBadge />
          </div>
        )}
        {hasEditionBadge && <EditionBadge />}
        {isPick && (
          <div className="absolute right-0 top-0 size-fit overflow-hidden rounded-bl-small rounded-tl-xxsmall">
            <Lottie src="/assets/jsons/pick-badge.json" />
          </div>
        )}
        {isAdult && (
          <div className="absolute bottom-0 right-0 rounded-br-small rounded-tl-small bg-B-500 px-8 py-2">
            <p className="font-600 text-Gray-white typo-caption2">19세 인증</p>
          </div>
        )}
        {isSoldOut && (
          <div
            className={cn(
              'absolute inset-0 rounded-xlarge bg-Gray-black/40 flex-center',
              isPick && 'rounded-tr-none',
              isAdult && 'rounded-br-small',
            )}
          >
            <span className="font-700 text-Gray-white typo-body4">
              품절되었어요
            </span>
          </div>
        )}
      </div>

      <div
        className={cn(
          'flex flex-col',
          isMobileRow && 'mobile-sm:min-w-0 mobile-sm:flex-1',
        )}
      >
        {institutionName ? (
          <p className="font-400 text-Gray-700 typo-caption1">
            {institutionName}
          </p>
        ) : null}

        {institution ? (
          <CustomLink
            prefetch={false}
            href={DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
              id: institution.id,
            })}
            aria-label={`${institution.name} 상세 페이지로 이동`}
            className={cn(
              'relative z-20 mb-4 flex w-fit items-center gap-4 rounded-xsmall',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500',
              'active:opacity-80',
            )}
          >
            <span className="relative size-20 overflow-hidden rounded-pill border border-Gray-300">
              <Image
                className="object-cover"
                src={institution.logoUrl || '/images/placeholder.png'}
                alt=""
                width={20}
                height={20}
                sizes="20px"
              />
            </span>
            <span className="mt-1 font-600 text-Gray-600 typo-caption1">
              {institution.name}
            </span>
            <SvgChevronright className="size-12 text-Gray-600" />
          </CustomLink>
        ) : null}

        {productName ? (
          <p
            className={cn(
              'line-clamp-2 min-h-[2lh] break-keep pt-4 font-400 text-Gray-black typo-body4',
              'transition-colors duration-200 group-hover:text-Gray-700',
            )}
          >
            {productName}
          </p>
        ) : null}

        {donationAmount ? (
          <div className="flex flex-wrap items-center gap-x-4 pt-4">
            <p className="font-700 text-Gray-black typo-body4">
              {donationAmount.toLocaleString()}원
            </p>
            <p className="font-400 text-Gray-700 typo-caption1">기부시 증정</p>
          </div>
        ) : null}

        {showRating ? (
          <Rating
            value={reviewRating ?? 0}
            count={reviewCount}
            size={ratingSize}
            className={cn('pt-4', isMobileRow && 'kakaobank:hidden')}
          />
        ) : null}
      </div>
    </article>
  );
};

const Loading = ({ isMobileRow }: { isMobileRow: boolean }) => {
  return (
    <div
      className={cn(
        'flex flex-col gap-8',
        isMobileRow &&
          'mobile-sm:flex-row mobile-sm:items-center mobile-sm:gap-16',
      )}
    >
      <Skeleton
        className={cn(
          'aspect-[1/1] size-full rounded-xlarge',
          isMobileRow && 'mobile-sm:size-116 mobile-sm:shrink-0',
        )}
      />

      <div
        className={cn(
          'flex flex-col',
          isMobileRow && 'mobile-sm:min-w-0 mobile-sm:flex-1',
        )}
      >
        <Skeleton className="h-24 w-100 rounded-xsmall mobile-tablet:h-20" />

        <Skeleton className="mt-4 h-48 w-full rounded-xsmall mobile-tablet:h-42" />

        <Skeleton
          typo="body2"
          className="mt-8 w-150 rounded-xsmall mobile-tablet:mt-4 mobile-tablet:typo-body4"
        />

        <Skeleton
          typo="body4"
          className={cn(
            'mt-4 w-108 rounded-xsmall mobile-tablet:typo-caption1',
            isMobileRow && 'kakaobank:hidden',
          )}
        />
      </div>
    </div>
  );
};

const NewBadge = () => {
  return (
    <span className="inline-flex items-center rounded-pill border border-Gray-black bg-Gray-white px-8 py-4 font-600 text-Gray-black typo-caption1">
      New
    </span>
  );
};

const EditionBadge = () => {
  return (
    <span
      className={cn(
        'absolute right-5 top-10 size-62',
        'mobile-sm:right-1 mobile-sm:top-8 mobile-sm:size-42',
      )}
    >
      <Image
        src={GOODS_HOME_IMAGE_URL.KAKAO_BADGE}
        alt="연말 한정 굿즈 다이어리 증정"
        className="object-contain"
        sizes="62px"
        //* 작은 일러스트라 기본 품질(50)이면 테두리가 뭉개진다 — 용량 부담이 적어 90으로 올린다
        quality={90}
        fill
      />
    </span>
  );
};
